import { isDef } from "@redotech/util/checks";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  Carriers,
  Service,
} from "../../fulfillments/fulfillment-carriers-and-services";
import {
  incrementToNextBusinessDay,
  incrementToNextValidDeliveryDate,
} from "./valid-delivery-date";

export const DELIVERY_DATE_VARIABLE = "{delivery_date}";
export const DYNAMIC_WEEKDAY_VARIABLE = "{dynamic_weekday}";
export const BUSINESS_DAYS_VARIABLE = "{business_days}";
export const BASE_DYNAMIC_WEEKDAY_REGEX = /\{(dynamic_weekday)\^?\}/g;
const UPPERCASE_DYNAMIC_WEEKDAY_REGEX = /\{(dynamic_weekday)\^\}/g;
export const BASE_BUSINESS_DAYS_REGEX = /\{(business_days)\}/g;

export function isDeliveryDateDisplayed(textTemplate: string) {
  return (
    textTemplate.includes(DELIVERY_DATE_VARIABLE) ||
    BASE_DYNAMIC_WEEKDAY_REGEX.test(textTemplate) ||
    BASE_BUSINESS_DAYS_REGEX.test(textTemplate)
  );
}

export enum DeliveryDateFormatStrategy {
  LOCALE_STRING_CUSTOM = "LOCALE_STRING_CUSTOM",
}

export const deliveryDateFormatWeekdayOptions = [
  "short",
  "long",
  "narrow",
] as const;
export const deliveryDateFormatMonthOptions = [
  "short",
  "long",
  "narrow",
  "numeric",
  "2-digit",
] as const;
export const deliveryDateFormatDayOptions = ["numeric", "2-digit"] as const;

export const rateTableTextFormatSchema = z.object({
  deliveryDateFormat: z
    .union([
      z.object({
        strategy: z.literal(DeliveryDateFormatStrategy.LOCALE_STRING_CUSTOM),
        weekday: createEnum(deliveryDateFormatWeekdayOptions, {
          enumName: "DeliveryDateFormatWeekdayOptions",
        }).optional(),
        month: createEnum(deliveryDateFormatMonthOptions, {
          enumName: "DeliveryDateFormatMonthOptions",
        }).optional(),
        day: createEnum(deliveryDateFormatDayOptions, {
          enumName: "DeliveryDateFormatDayOptions",
        }).optional(),
      }),
    ])
    .optional(),
  range: z
    .object({
      startRelativeToDeliveryDate: z.number().int(),
      endRelativeToDeliveryDate: z.number().int(),
    })
    .optional(),
});
export type RateTableTextFormat = z.infer<typeof rateTableTextFormatSchema>;

export const defaultRateTableTextFormat: RateTableTextFormat = {
  deliveryDateFormat: undefined,
};

export function formatRateTableDisplayText({
  textTemplate,
  deliveryDate,
  now,
  locale,
  rateTableTextFormat,
  carrier,
  service,
}: {
  textTemplate: string;
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
}) {
  return textTemplate
    .replaceAll(
      DELIVERY_DATE_VARIABLE,
      formatDeliveryDate({
        deliveryDate,
        now,
        locale,
        rateTableTextFormat,
        carrier,
        service,
      }),
    )
    .replaceAll(BASE_DYNAMIC_WEEKDAY_REGEX, (match) => {
      return formatDynamicWeekday({
        deliveryDate,
        now,
        locale,
        matchText: match,
      });
    })
    .replaceAll(BASE_BUSINESS_DAYS_REGEX, () => {
      return formatBusinessDays({
        deliveryDate,
        now,
        rateTableTextFormat,
        carrier,
        service,
      });
    });
}

function formatDeliveryDate({
  deliveryDate,
  now,
  locale,
  rateTableTextFormat,
  carrier,
  service,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
}) {
  function formatDeliveryDateWithCustomFormat({
    dateToFormat,
  }: {
    dateToFormat: Temporal.ZonedDateTime;
  }) {
    if (isDef(rateTableTextFormat.deliveryDateFormat)) {
      return dateToFormat.toLocaleString(locale, {
        weekday: rateTableTextFormat.deliveryDateFormat.weekday,
        month: rateTableTextFormat.deliveryDateFormat.month,
        day: rateTableTextFormat.deliveryDateFormat.day,
      });
    } else {
      return dateToFormat.toLocaleString(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  }
  if (isDef(rateTableTextFormat.range)) {
    const rangeDifference =
      rateTableTextFormat.range.endRelativeToDeliveryDate -
      rateTableTextFormat.range.startRelativeToDeliveryDate;

    // Calculate initial start date, but ensure it's never before 'now'
    const initialStartDate = deliveryDate.add({
      days: rateTableTextFormat.range.startRelativeToDeliveryDate,
    });
    const adjustedStartDate =
      Temporal.ZonedDateTime.compare(initialStartDate, now) < 0
        ? now
        : initialStartDate;

    const startDate = incrementToNextValidDeliveryDate({
      date: adjustedStartDate,
      carrier,
      service,
    });
    const endDate = incrementToNextValidDeliveryDate({
      date: startDate.add({ days: rangeDifference }),
      carrier,
      service,
    });
    // Check if start and end dates are the same day
    const startPlainDate = startDate.toPlainDate();
    const endPlainDate = endDate.toPlainDate();
    const isSameDay = startPlainDate.equals(endPlainDate);

    if (isSameDay) {
      // Display as single date when range is same day
      return formatDeliveryDateWithCustomFormat({ dateToFormat: startDate });
    } else {
      // Display as range when dates are different
      const formattedStart = formatDeliveryDateWithCustomFormat({
        dateToFormat: startDate,
      });
      const formattedEnd = formatDeliveryDateWithCustomFormat({
        dateToFormat: endDate,
      });
      return `${formattedStart} - ${formattedEnd}`;
    }
  } else {
    return formatDeliveryDateWithCustomFormat({ dateToFormat: deliveryDate });
  }
}

function calculateBusinessDays(
  from: Temporal.ZonedDateTime,
  to: Temporal.ZonedDateTime,
): number {
  let count = 0;
  let current = from.toPlainDate();
  const endDate = to.toPlainDate();

  while (current.until(endDate).total({ unit: "day" }) > 0) {
    current = current.add({ days: 1 });
    const dayOfWeek = current.dayOfWeek;
    // Monday = 1, Sunday = 7, so weekdays are 1-5
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      count++;
    }
  }

  return count;
}

function formatBusinessDays({
  deliveryDate,
  now,
  rateTableTextFormat,
  carrier,
  service,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  rateTableTextFormat: RateTableTextFormat;
  carrier: Carriers;
  service: Service;
}) {
  function getBusinessDaysText(businessDays: number) {
    return `${businessDays} business day${businessDays !== 1 ? "s" : ""}`;
  }

  function getBusinessDaysRangeText(minDays: number, maxDays: number) {
    if (minDays === maxDays) {
      return getBusinessDaysText(minDays);
    } else {
      return `${minDays}-${maxDays} business days`;
    }
  }

  if (isDef(rateTableTextFormat.range)) {
    const rangeDifference =
      rateTableTextFormat.range.endRelativeToDeliveryDate -
      rateTableTextFormat.range.startRelativeToDeliveryDate;

    // Calculate initial start date, but ensure it's never before 'now'
    const initialStartDate = deliveryDate.add({
      days: rateTableTextFormat.range.startRelativeToDeliveryDate,
    });
    const adjustedStartDate =
      Temporal.ZonedDateTime.compare(initialStartDate, now) < 0
        ? now
        : initialStartDate;

    const startDate = incrementToNextBusinessDay({
      date: adjustedStartDate,
      carrier,
    });
    const endDate = incrementToNextBusinessDay({
      date: startDate.add({ days: rangeDifference }),
      carrier,
    });

    const startBusinessDays = calculateBusinessDays(now, startDate);
    const endBusinessDays = calculateBusinessDays(now, endDate);

    return getBusinessDaysRangeText(startBusinessDays, endBusinessDays);
  } else {
    const businessDays = calculateBusinessDays(now, deliveryDate);
    return getBusinessDaysText(businessDays);
  }
}

function formatDynamicWeekday({
  deliveryDate,
  now,
  locale,
  matchText,
}: {
  deliveryDate: Temporal.ZonedDateTime;
  now: Temporal.ZonedDateTime;
  locale: string;
  matchText: string;
}) {
  const relativeDateFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });
  function getFormattedRelativeDate() {
    const weekday = deliveryDate.toLocaleString(locale, { weekday: "long" });
    const currentDayOfWeek =
      now.dayOfWeek as keyof typeof CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT;
    const daysBetweenNowAndDeliveryDate = deliveryDate.since(now, {
      largestUnit: "days",
    }).days;
    const relativeDateTemplateMapForCurrentDayOfWeek =
      CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT[
        currentDayOfWeek
      ];
    if (daysBetweenNowAndDeliveryDate < 0) {
      return relativeDateTemplateMapForCurrentDayOfWeek[0].replace(
        WEEKDAY_PLACEHOLDER,
        weekday,
      );
    } else if (
      daysBetweenNowAndDeliveryDate in
      relativeDateTemplateMapForCurrentDayOfWeek
    ) {
      return relativeDateTemplateMapForCurrentDayOfWeek[
        daysBetweenNowAndDeliveryDate as keyof typeof relativeDateTemplateMapForCurrentDayOfWeek
      ].replace(WEEKDAY_PLACEHOLDER, weekday);
    } else {
      return relativeDateFormatter.format(
        daysBetweenNowAndDeliveryDate,
        "days",
      );
    }
  }

  const formattedRelativeDate = getFormattedRelativeDate();

  if (UPPERCASE_DYNAMIC_WEEKDAY_REGEX.test(matchText)) {
    return (
      formattedRelativeDate.charAt(0).toUpperCase() +
      formattedRelativeDate.slice(1)
    );
  } else {
    return formattedRelativeDate;
  }
}

const WEEKDAY_PLACEHOLDER = "{weekday}";
/**
 * Outer keys represent the day of the week of the current date.
 *
 * Inner keys represent the number of days between the current date and the
 * delivery date.
 */
const CURRENT_DATE_AND_DELIVERY_DATE_PAIR_TO_RELATIVE_DATE_FORMAT = {
  // Monday
  1: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `this ${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
    10: `next week ${WEEKDAY_PLACEHOLDER}`,
    11: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Tuesday
  2: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
    10: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Wednesday
  3: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
    9: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Thursday
  4: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
    8: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Friday
  5: {
    0: "today",
    1: "tomorrow",
    2: `${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next week ${WEEKDAY_PLACEHOLDER}`,
  },
  // Saturday
  6: {
    0: "today",
    1: "tomorrow",
    2: `${WEEKDAY_PLACEHOLDER}`,
    3: `${WEEKDAY_PLACEHOLDER}`,
    4: `${WEEKDAY_PLACEHOLDER}`,
    5: `${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: "next Saturday",
  },
  // Sunday
  7: {
    0: "today",
    1: "tomorrow",
    2: `this ${WEEKDAY_PLACEHOLDER}`,
    3: `this ${WEEKDAY_PLACEHOLDER}`,
    4: `this ${WEEKDAY_PLACEHOLDER}`,
    5: `this ${WEEKDAY_PLACEHOLDER}`,
    6: `${WEEKDAY_PLACEHOLDER}`,
    7: `next ${WEEKDAY_PLACEHOLDER}`,
  },
} as const;
